import React, { useEffect, useState } from "react";
import CustomTextField from "../../components/ReUsableComponents/CustomTextField";
import { LuUser } from "react-icons/lu";
import { RiEyeCloseLine } from "react-icons/ri";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { setEmailError } from "../../redux/Slices/loginSlice";
import { useDispatch } from "react-redux";
import { GrCircleAlert } from "react-icons/gr";
import { useSignupMutation } from "../../redux/API/Login/loginApi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { setItem } from "../../utils/token";
import { usePasswordValidation } from "../../utils";
import AuthButton from "./LoginButton";
import logoNew from "../../assets/Images/VerticalDarkLogo1.svg";
import sat from "../../assets/Images/SATprep1.svg";
import OnboardingBackground from "../../components/ReUsableComponents/OnboardingBackground";



const SignUpForm = () => {
  const emailPattern = /^[a-zA-Z0-9][^\s@]*@[^\s@]+\.[a-zA-Z]+$/;


  const [signUpData, setSignUpData] = useState({
    userName: "",
    confirmUserName: "",
    password: "",
    confirmPassword: "",
  });

  const { userName, confirmUserName, password, confirmPassword } = signUpData;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { validatePassword } = usePasswordValidation();

  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const [signup, { isLoading }] = useSignupMutation();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const { name, value } = e.target;
    setError("");
    setSignUpData((prevState) => ({
      ...prevState,
      [name]: value?.trim(),
    }));
  };

  const validateEmail = (email: string) => {
    if (!emailPattern.test(email) && email) {
      dispatch(setEmailError("Enter a valid email"));
      return false;
    } else {
      dispatch(setEmailError(""));
    }
    return true;
  };

  const handleError = (error: any) => {
    toast.error(error?.data);
  };

  const handleSignUp = async () => {
  const isEmailValid = validateEmail(userName);
  const isEmailsMatch = userName === confirmUserName;
  const isPasswordsMatch = password === confirmPassword;
  const isPasswordvalid = validatePassword(password);

    if (
      !(isEmailValid && isEmailsMatch && isPasswordsMatch && isPasswordvalid)
    ) {
      if (!isEmailValid) {
        setError("Enter a valid email");
      } else if (!isEmailsMatch) {
        setError("Emails do not match");
      } else if (!isPasswordsMatch) {
        setError("Passwords do not match");
      } else if (!isPasswordvalid) {
        setError(
          "Password must be at least 8 characters long, contain at least one special character, and one uppercase letter."
        );
      } else {
        setError("");
      }
      return;
    }

    try {
      const payload = {
        userName: userName.trim().toLowerCase(),
        password: password.trim(),
      };
      const res: any = await signup(payload).unwrap();
      if (res.status === "success") {
        setItem("userEmail", userName);
        setItem("userId", res?.data?.id);
        navigate("/verifyemail");
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  const disableSignup = () => {
    if (
      !userName ||
      !confirmUserName ||
      !password ||
      !confirmPassword ||
      isLoading
    ) {
      return true;
    } else return false;
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  useEffect(() => {
    if (userName && confirmUserName && password && confirmPassword) {
      const handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === "Enter") {
          handleSignUp();
        }
      };
      window.addEventListener("keydown", handleKeyPress);
      return () => {
        window.removeEventListener("keydown", handleKeyPress);
      };
    }
  }, [signUpData]);


  return (
    <OnboardingBackground>
      <div className="flex flex-col items-center w-[720px] p-8 rounded-2xl shadow-xl border bg-white gap-6">
        <div>
          <img src={logoNew} className="w-[260px] h-[163px]" alt="FullMarks Logo" />
          <img src={sat} className="w-[211px] h-[68px] mx-auto" alt="SATprep Logo" />
        </div>
        <div className="w-[450px]">
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 border border-red-500 bg-red-100 rounded-md">
              <p className="text-red-600 text-sm font-semibold">{error}</p>
            </div>
          )}
  
          <CustomTextField 
            name="userName" 
            placeholder="Email" 
            type="email" 
            value={signUpData.userName} 
            onChangeHandler={handleChange} 
            className="mb-4 w-full" 
          />
  
          <CustomTextField 
            name="confirmUserName" 
            placeholder="Confirm Email" 
            type="email" 
            value={signUpData.confirmUserName} 
            onChangeHandler={handleChange} 
            className="mb-4 w-full" 
          />
  
          {/* Password Input */}
          <div className="relative mb-4 w-full">
            <CustomTextField 
              name="password" 
              placeholder="Password" 
              type={showPassword ? "text" : "password"} 
              value={signUpData.password} 
              onChangeHandler={handleChange} 
              className="w-full"
            />
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
              aria-label="Toggle password visibility"
            >
              {showPassword ? <MdOutlineRemoveRedEye /> : <RiEyeCloseLine />}
            </button>
          </div>
  
          {/* Confirm Password Input */}
          <div className="relative mb-4 w-full">
            <CustomTextField 
              name="confirmPassword" 
              placeholder="Confirm Password" 
              type={showConfirmPassword ? "text" : "password"} 
              value={signUpData.confirmPassword} 
              onChangeHandler={handleChange} 
              className="w-full"
            />
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label="Toggle confirm password visibility"
            >
              {showConfirmPassword ? <MdOutlineRemoveRedEye /> : <RiEyeCloseLine />}
            </button>
          </div>
  
          {/* Sign Up Button */}
          <div className="flex justify-center">
            <AuthButton 
              text="Sign Up" 
              icon={<LuUser />} 
              onClick={handleSignUp} 
              loader={isLoading} 
              disabled={disableSignup()} 
              className="" 
            />
          </div>
          <p className="text-center mt-9 text-sm text-satPrepBlack font-gully">
            Already have an account? <button className="underline text-satPrepDarkAccent cursor-pointer" onClick={() => navigate("/login")}>Sign in!</button>
          </p>
        </div>
      </div>
    </OnboardingBackground>
  );
}
  
export default SignUpForm;